import { useMemo, type ReactNode } from 'react';
import { ScrollText } from 'lucide-react';
// Importa il CHANGELOG.md dalla root del repo come testo grezzo (Vite ?raw).
import changelogRaw from '../../../../../CHANGELOG.md?raw';

// Rende il grassetto **testo** e il codice `testo` inline senza dipendenze esterne.
function renderInline(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="font-semibold text-zinc-100">{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return <code key={i} className="rounded bg-zinc-800/80 px-1 py-0.5 text-[0.85em] text-accent-200">{p.slice(1, -1)}</code>;
    }
    // Rimuove la sintassi link markdown lasciando solo il testo
    return <span key={i}>{p.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')}</span>;
  });
}

// Colori badge per tipo di sezione (Aggiunto/Corretto/Modificato)
const SECTION_STYLE: Record<string, string> = {
  'Nuove implementazioni': 'bg-emerald-500/15 text-emerald-300',
  'Bug risolti': 'bg-rose-500/15 text-rose-300',
};

/**
 * Vista "Novità": mostra il CHANGELOG.md del progetto con un mini-parser
 * markdown (titoli versione, sezioni, elenchi) — nessuna libreria aggiuntiva.
 */
export function ChangelogView() {
  const blocks = useMemo(() => {
    const out: ReactNode[] = [];
    let list: ReactNode[] = [];
    let key = 0;
    const flushList = () => {
      if (list.length) {
        out.push(<ul key={key++} className="mb-3 space-y-1.5 pl-1">{list}</ul>);
        list = [];
      }
    };
    for (const raw of changelogRaw.split('\n')) {
      const line = raw.trimEnd();
      if (line.startsWith('# ')) continue; // titolo documento: già nell'header della vista
      if (line === '---') { flushList(); continue; }
      if (line.startsWith('## ')) {
        flushList();
        // Es: "[2.0.0] — 2026-07-17" → versione + data
        const m = line.slice(3).match(/^\[([^\]]+)\]\s*—\s*(.*)$/);
        out.push(
          <div key={key++} className="mb-2 mt-8 flex items-baseline gap-3 border-b border-zinc-800/80 pb-2 first:mt-0">
            <span className="text-lg font-bold text-accent-300">v{m ? m[1] : line.slice(3)}</span>
            {m && <span className="text-xs text-zinc-500">{m[2]}</span>}
          </div>,
        );
        continue;
      }
      if (line.startsWith('### ')) {
        flushList();
        const label = line.slice(4);
        out.push(
          <span key={key++} className={'mb-2 mt-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ' + (SECTION_STYLE[label] ?? 'bg-zinc-700/40 text-zinc-300')}>
            {label}
          </span>,
        );
        continue;
      }
      if (line.startsWith('- ')) {
        list.push(
          <li key={key++} className="flex gap-2 text-sm text-zinc-300">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-600" />
            <span>{renderInline(line.slice(2))}</span>
          </li>,
        );
        continue;
      }
      flushList();
      if (line.trim()) {
        out.push(<p key={key++} className="mb-2 text-sm text-zinc-400">{renderInline(line)}</p>);
      }
    }
    flushList();
    return out;
  }, []);

  return (
    <div className="flex flex-1 overflow-hidden">
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-8">
          <div className="mb-1 flex items-center gap-2">
            <ScrollText size={20} className="text-accent-300" />
            <h1 className="text-xl font-bold text-zinc-100">Novità</h1>
          </div>
          <p className="mb-6 text-sm text-zinc-500">Cronologia degli aggiornamenti della piattaforma.</p>
          {blocks}
        </div>
      </main>
    </div>
  );
}
